import { useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import { Icon, IconButton, Tooltip } from '@atom/mui';
import { ArrowDropDownIcon, ArrowRightIcon, FolderIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import {
  AnalyticsFolderTree as FolderTree,
  SELECTED_BORDER_WIDTH,
} from '@atom/types/analytics';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';

import AnalyticsContext from './AnalyticsContext';
import FolderOptions from './FolderOptions';

import classes from './analyticsFolderTree.module.css';

const PADDING_OFFSET = 2;

const styles = {
  iconStyle: {
    fontSize: '24px',
    margin: 0,
  },
  blankStyle: {
    boxSizing: 'border-box',
    padding: '24px',
  },
};

export interface Props {
  folder: FolderTree;
  indentation: number;
}

const AnalyticsFolderRow = ({ folder, indentation }: Props) => {
  const {
    openFolders,
    toggleOpen,
    setActiveFolderId,
    activeFolderId,
    setIsActiveFolderLeafNode,
    setSearching,
  } = useContext(AnalyticsContext);
  const expanded = openFolders.has(folder.id);
  const [overflowing, setOverflowing] = useState<boolean>(false);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const { offsetWidth, scrollWidth } = container.current;
    setOverflowing(offsetWidth < scrollWidth);
  }, [folder.name]);

  useEffect(() => {
    if (activeFolderId === folder.id) {
      setIsActiveFolderLeafNode(R.isEmpty(folder.children));
    }
  }, [setIsActiveFolderLeafNode, folder.children, activeFolderId, folder.id]);

  const arrowDirection = expanded ? ArrowDropDownIcon : ArrowRightIcon;

  const leftIcon = (
    <>
      {!R.isEmpty(folder.children) ? (
        <IconButton
          onClick={() => toggleOpen(folder.id)}
          style={styles.iconStyle}
          data-cy={`analyticsFolderRowToggle-${folder.name}`}
        >
          <Icon color={colors.neutral.dim} icon={arrowDirection} />
        </IconButton>
      ) : (
        <div style={styles.blankStyle} />
      )}
      <Icon
        style={styles.iconStyle}
        color={colors.neutral.gray}
        icon={FolderIcon}
      />
    </>
  );

  const rowStyle = clsx(
    classes.folderRow,
    folder.id === activeFolderId && classes.selected,
  );

  const isActiveFolder = activeFolderId === folder.id;

  const containerStyle = {
    paddingLeft: `calc(${
      isActiveFolder ? `1rem - ${SELECTED_BORDER_WIDTH}` : '1rem'
    } + ${PADDING_OFFSET * indentation}rem)`,
    borderLeft: isActiveFolder
      ? `${SELECTED_BORDER_WIDTH} solid ${colors.brand.blue}`
      : 'none',
  };

  const onClick = () => {
    setActiveFolderId(folder.id);
    setIsActiveFolderLeafNode(R.isEmpty(folder.children));
    setSearching(false);
  };

  const overflowStyle = {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    width: `calc(12rem - (1rem + ${PADDING_OFFSET * indentation}rem))`,
  };

  const showFolderOptions = hasRolePermissions(ROLE_SETS.ORG_ADMIN);

  return (
    <>
      <div className={rowStyle} style={containerStyle} onClick={onClick}>
        <div className={classes.folderNameContainer}>
          {leftIcon}
          <div ref={container} className={classes.rowNameContainer}>
            <div>
              {!overflowing ? (
                folder.name
              ) : (
                <Tooltip
                  lightTooltip
                  title={folder.name}
                  placement="right-start"
                >
                  <p style={overflowStyle}>{folder.name}</p>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
        {showFolderOptions && <FolderOptions folder={folder} />}
      </div>
      {expanded ? (
        folder.children.map((child: FolderTree) => {
          return (
            <AnalyticsFolderRow
              key={child.id}
              folder={child}
              indentation={indentation + 1}
            />
          );
        })
      ) : (
        <div />
      )}
    </>
  );
};

export default AnalyticsFolderRow;
